import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  Popconfirm,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import GenericCard from "components/UI/GenericCard/GenericCard";
import ProtectedContent from "components/UI/ProtectedContent";
import AntTable from "components/UI/AntTable";
import {
  apiDismissPriceAdjustment,
  apiGetPriceAdjustments,
  apiScanPriceAdjustments,
} from "api/apiAdmin";
import SplitFixModal from "./SplitFixModal";

// Splits / IBKR price re-adjustments (backend api/price-adjustments).
// New splits are detected automatically (pre-open + daily) and frozen; the
// scan finds splits already in the data. Each symbol is fixed here.
const STATUS_COLORS = {
  detected: "blue",
  frozen: "red",
  repaired: "green",
  dismissed: "default",
};
const FIX_COLORS = {
  running: "processing",
  done: "success",
  failed: "error",
  interrupted: "warning",
};
const SOURCES = {
  daily: "Daily run",
  preopen: "Pre-open check",
  autotrader: "AutoTrader",
  scan: "Scan",
};
const OPEN_STATUSES = ["detected", "frozen"];

const isOpen = (record) => OPEN_STATUSES.includes(record.status);

const FixLog = ({ fix }) => (
  <div className="max-h-64 overflow-auto">
    {fix?.error && (
      <Typography.Text type="danger">Error: {fix.error}</Typography.Text>
    )}
    {(fix?.log || [])
      .slice()
      .reverse()
      .map((line, i) => (
        <div key={i}>
          <Typography.Text type="secondary">
            {dayjs(line.time).format("DD/MM HH:mm:ss")}
          </Typography.Text>{" "}
          {line.message}
        </div>
      ))}
  </div>
);

const findingText = (record) => {
  const f = record.findings;
  if (f?.type === "candles") {
    return `Candles jump ${f.prev} -> ${f.close} on ${f.day}`;
  }
  if (f?.type === "predictions") {
    return `${f.badDays} prediction days in old prices (${f.firstBad} -> ${f.lastBad})${f.badModels ? ` in ${f.badModels} model(s)` : ""}, candle / prediction price x${f.typicalFactor}`;
  }
  if (record.ibkrClose) {
    return `Stored close ${record.storedClose}, IBKR now ${record.ibkrClose}`;
  }
  return record.evidence
    ? `IB ${record.evidence.ibShares} shares vs ${record.evidence.dbShares}, price ${record.evidence.price} vs ${record.evidence.referenceRate}`
    : "";
};

const Splits = () => {
  const [records, setRecords] = useState([]);
  const [running, setRunning] = useState(null);
  const [scanFrom, setScanFrom] = useState(null);
  const [filter, setFilter] = useState("open");
  const [scanning, setScanning] = useState(false);
  const [fixSymbol, setFixSymbol] = useState(null);

  const fetchRecords = useCallback(async () => {
    try {
      const response = await apiGetPriceAdjustments();
      setRecords(response.records);
      setRunning(response.running);
      setScanFrom(response.scanFrom);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  }, []);

  useEffect(() => {
    document.title = "Splits | Dindicator Dashboard";
    fetchRecords();
  }, [fetchRecords]);

  // follow a running fix
  useEffect(() => {
    if (!running) return undefined;
    const interval = setInterval(fetchRecords, 3000);
    return () => clearInterval(interval);
  }, [running, fetchRecords]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const { found, added, updated, removed } =
        await apiScanPriceAdjustments();
      message.success(
        `Scan done: ${found} finding(s) -- ${added} new, ${updated} refreshed, ${removed} no longer found`,
      );
      await fetchRecords();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    } finally {
      setScanning(false);
    }
  };

  const handleDismiss = async (record) => {
    try {
      await apiDismissPriceAdjustment(record._id);
      message.success(`${record.symbol} ${record.date} dismissed`);
      fetchRecords();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const visible = useMemo(
    () => (filter === "open" ? records.filter(isOpen) : records),
    [records, filter],
  );

  const columns = [
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: (a, b) => a.symbol.localeCompare(b.symbol),
    },
    {
      title: "Event",
      dataIndex: "event",
      render: (text, record) => (
        <Tooltip title={findingText(record)}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: "Shares per old share",
      dataIndex: "ratio",
      render: (ratio) => (ratio ? +ratio.toFixed(4) : "not a split"),
    },
    {
      title: "Source",
      dataIndex: "source",
      render: (source) => SOURCES[source] || source,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status]}>
          {status === "frozen" ? "frozen (automation paused)" : status}
        </Tag>
      ),
    },
    {
      title: "Fix",
      dataIndex: "fix",
      render: (fix) =>
        fix ? (
          <Tooltip title={fix.error}>
            <Tag color={FIX_COLORS[fix.state]}>
              {fix.state === "running" ? fix.step : fix.state}
            </Tag>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Detected",
      dataIndex: "detectedAt",
      render: (text) => dayjs(text).format("DD/MM/YY HH:mm"),
      sorter: (a, b) => new Date(a.detectedAt) - new Date(b.detectedAt),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) =>
        isOpen(record) && (
          <Space>
            <Button
              type="primary"
              size="small"
              disabled={!!running}
              onClick={() => setFixSymbol(record.symbol)}
            >
              Preview & fix
            </Button>
            <Popconfirm
              title={`Dismiss ${record.symbol} ${record.date}?`}
              description="Not a split (or handled by hand): removes this finding from the list without changing anything. The symbol's other findings stay."
              onConfirm={() => handleDismiss(record)}
            >
              <Button size="small" disabled={running === record.symbol}>
                Dismiss
              </Button>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <>
      <Divider orientation="left">Splits</Divider>
      <div className="cards-wrapper">
        <ProtectedContent>
          <GenericCard title="Splits & price re-adjustments">
            <Typography.Paragraph type="secondary">
              New splits are detected automatically (8:30 ET pre-open check of
              symbols with trades, the daily runs, and AutoTrader) and their
              symbol is frozen: no orders, intraday rules or strategy updates
              until it is fixed. A frozen symbol without open or pending trades
              is fixed automatically; one with trades waits for you to enter
              the split ratio here (check it online -- the detected one is a
              hint). Scan finds splits already in the data
              (candle jumps, prediction prices in old units) from {scanFrom}{" "}
              on -- older candles are what IBKR returns, a re-pull can&apos;t
              change them. The fix re-pulls
              the candles if needed, regenerates all models' predictions,
              adjusts open trades and strategy prices to the new share count,
              and unfreezes the symbol.
            </Typography.Paragraph>
            <Space className="mb-4" wrap>
              <Button type="primary" loading={scanning} onClick={handleScan}>
                Scan for splits
              </Button>
              <Button onClick={fetchRecords}>Refresh</Button>
              <Segmented
                value={filter}
                onChange={setFilter}
                options={[
                  { label: "To fix", value: "open" },
                  { label: "All", value: "all" },
                ]}
              />
              {running && <Tag color="processing">Fixing {running}...</Tag>}
            </Space>
            <AntTable
              rowKey="_id"
              dataSource={visible}
              columns={columns}
              expandable={{
                expandedRowRender: (record) => <FixLog fix={record.fix} />,
                rowExpandable: (record) => !!record.fix,
              }}
              // expanded logs have varying heights
              virtual={false}
              scroll={{ x: 900, y: 600 }}
            />
          </GenericCard>
        </ProtectedContent>
      </div>
      {fixSymbol && (
        <SplitFixModal
          symbol={fixSymbol}
          onClose={() => setFixSymbol(null)}
          onStarted={() => {
            setFixSymbol(null);
            fetchRecords();
          }}
        />
      )}
    </>
  );
};

export default Splits;
